import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import { TESTID_COMPONENT } from 'constants/data-testid';
import Spinner, { spinnerColors, spinnerSizes } from 'components/Spinner';
import { Paper } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import {
  FieldLabel,
  FieldWrap,
  FieldError,
  FieldGroup,
  sizes,
  FieldLabelWrap,
  RequiredLabel,
  FieldSpinnerWrap,
  FieldIconWrap,
  FieldWrapOuter,
  FieldHint,
  Dropdown,
  OptionWrap,
} from './styles';
import { validateValueType } from './helpers';
import { FIELD_VALUE_TYPES, FIELD_TYPES, FIELD_ALIGN } from './constants';

const FIELD_SIZES = sizes;

class Field extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      stateValue: props.value || '',
      isDropdownOpen: false,
    };
    this.inputRef = React.createRef();
    this.closeDropdownOnBlur = _debounce(this.closeDropdownOnBlur, 200);
  }

  componentDidMount() {
    const { focusOnMount } = this.props;
    if (focusOnMount) {
      this.inputRef.focus();
    }
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;

    if (prevProps.value !== value) {
      this.changeStateValue(value);
    }
  }

  setWrapperRef = node => {
    this.wrapperRef = node;
  }

  setInputRef = node => {
    const { forwardedRef } = this.props;
    this.inputRef = node;
    if (forwardedRef) {
      forwardedRef(node);
    }
  }

  setIsDropdownOpen = isDropdownOpen => {
    this.setState({ isDropdownOpen });
  }

  changeStateValue = stateValue => {
    this.setState({
      stateValue,
    });
  }

  handleOnChange = e => {
    const { target: { value } } = e;
    const { onChange, valueType } = this.props;
    const { stateValue } = this.state;
    this.setIsDropdownOpen(true);
    const validTypeValue = validateValueType(valueType, value);
    const newValue = validTypeValue ? value : stateValue;

    if (validTypeValue) {
      this.changeStateValue(newValue);
      onChange(newValue);
    }
  }

  handleAutocompleteOptionClick = option => {
    const { onChange, onAutocomplete } = this.props;
    this.changeStateValue(option);
    this.setIsDropdownOpen(false);
    onChange(option);
    onAutocomplete(option);
  }

  closeDropdownOnBlur = () => {
    this.setIsDropdownOpen(false);
  }

  handleOnBlur = () => {
    const { isDropdownOpen } = this.state;
    if (isDropdownOpen) {
      this.closeDropdownOnBlur();
    }
  }

  render() {
    const {
      label,
      value,
      placeholder,
      type,
      onChange,
      onAutocomplete,
      width,
      minWidth,
      maxWidth,
      size,
      disabled,
      align,
      valueType,
      hasError,
      error,
      hint,
      isOptional,
      isRequired,
      focusOnMount,
      forwardedRef,
      isControlled,
      isLoading,
      prefixIcon: PrefixIcon,
      suffixIcon: SuffixIcon,
      children,
      autoComplete,
      autocompleteOptions,
      ...inputProps
    } = this.props;
    const { stateValue, isDropdownOpen } = this.state;
    const inputValue = isControlled ? value : stateValue;

    return (
      <FieldWrapOuter
        width={width}
        minWidth={minWidth}
        maxWidth={maxWidth}
      >
        {label && (
          <FieldLabelWrap>
            {label && <FieldLabel>{label}</FieldLabel>}
            {isRequired && <RequiredLabel>*</RequiredLabel>}
            {!isRequired && isOptional && <FieldHint>(Optional)</FieldHint>}
          </FieldLabelWrap>
        )}
        <FieldWrap
          size={size}
          disabled={disabled}
          align={align}
          hasError={hasError || !!error}
        >
          {PrefixIcon && (
            <FieldIconWrap>
              <PrefixIcon />
            </FieldIconWrap>
          )}

          <input
            {...inputProps}
            type={type}
            value={inputValue}
            placeholder={placeholder}
            onChange={this.handleOnChange}
            onBlur={this.handleOnBlur}
            disabled={disabled}
            autoComplete={autoComplete ? 'on' : 'new-password'}
            ref={this.setInputRef}
          />

          {isLoading && (
            <FieldSpinnerWrap>
              <Spinner
                size={spinnerSizes.small}
                color={disabled ? spinnerColors.dark : spinnerColors.default}
              />
            </FieldSpinnerWrap>
          )}

          {SuffixIcon && (
            <FieldIconWrap>
              <SuffixIcon />
            </FieldIconWrap>
          )}

          {children}
        </FieldWrap>
        {error && <FieldError>{error}</FieldError>}
        {!error && hint && <FieldHint>{hint}</FieldHint>}
        {!_isEmpty(autocompleteOptions) && isDropdownOpen && (
          <Dropdown
            open={isDropdownOpen}
            data-testid={TESTID_COMPONENT.FIELD_AUTOCOMPLETE_DROPDOWN}
          >
            <Paper elevation={3} ref={this.setDropdownRef}>
              {autocompleteOptions.map(option => (
                <OptionWrap
                  key={option}
                  onClick={() => this.handleAutocompleteOptionClick(option)}
                  data-testid={TESTID_COMPONENT.FIELD_AUTOCOMPLETE_OPTION(option)}
                >
                  <MenuItem>
                    {option}
                  </MenuItem>
                </OptionWrap>
              ))}
            </Paper>
          </Dropdown>
        )}

      </FieldWrapOuter>
    );
  }
}

Field.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(Object.values(FIELD_TYPES)),
  onChange: PropTypes.func,
  width: PropTypes.string,
  minWidth: PropTypes.string,
  maxWidth: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(sizes)),
  disabled: PropTypes.bool,
  align: PropTypes.oneOf(Object.values(FIELD_ALIGN)),
  valueType: PropTypes.oneOf(Object.values(FIELD_VALUE_TYPES)),
  hasError: PropTypes.bool,
  error: PropTypes.string,
  hint: PropTypes.string,
  focusOnMount: PropTypes.bool,
  forwardedRef: PropTypes.func,
  isControlled: PropTypes.bool,
  isRequired: PropTypes.bool,
  isOptional: PropTypes.bool,
  label: PropTypes.string,
  isLoading: PropTypes.bool,
  prefixIcon: PropTypes.elementType,
  suffixIcon: PropTypes.elementType,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  autoComplete: PropTypes.bool,
  autocompleteOptions: PropTypes.arrayOf(PropTypes.string),
  onAutocomplete: PropTypes.func,
};

Field.defaultProps = {
  value: '',
  placeholder: '',
  type: 'text',
  onChange() {},
  width: null,
  minWidth: null,
  maxWidth: null,
  size: sizes.medium,
  disabled: false,
  align: FIELD_ALIGN.LEFT,
  valueType: FIELD_VALUE_TYPES.ANY,
  hasError: false,
  error: null,
  hint: null,
  focusOnMount: false,
  forwardedRef() {},
  isControlled: false,
  isRequired: false,
  isOptional: false,
  label: null,
  isLoading: false,
  prefixIcon: null,
  suffixIcon: null,
  children: null,
  autoComplete: false,
  autocompleteOptions: [],
  onAutocomplete() {},
};

export default Field;

export {
  FieldLabel,
  FieldError,
  FieldHint,
  FieldGroup,
  FIELD_SIZES,
  FIELD_VALUE_TYPES,
  FIELD_TYPES,
  FIELD_ALIGN,
};
